/**
 * Landing — reproduction 1:1 de src/pages/Index.tsx du repo Loveable
 * fiberfriend-portal (avec adaptations : Link to="/signup" au lieu de
 * "/register", et import MarketingLayout pour wrapper Header + Footer).
 */
import {
  ArrowRight,
  Briefcase,
  Building,
  CheckCircle2,
  Download,
  FileCheck,
  Home,
  PhoneCall,
  Shield,
  Sparkles,
  Upload,
  Users,
} from 'lucide-react';
import { Link, Navigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth-context';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { MarketingLayout } from '@/components/site/marketing-layout';

const benefits = [
  {
    title: 'Débit ultra-rapide',
    description:
      "Profitez de vitesses de téléchargement exceptionnelles jusqu'à 8 Gbits/s.",
    icon: Download,
  },
  {
    title: 'Faible latence',
    description:
      'Idéal pour le gaming et les vidéoconférences avec une réactivité optimale.',
    icon: Sparkles,
  },
  {
    title: 'Connexion stable',
    description:
      'Une connexion fiable grâce à notre infrastructure de dernière génération.',
    icon: CheckCircle2,
  },
  {
    title: 'Sécurité renforcée',
    description:
      'Protection avancée contre les cyber-menaces pour votre tranquillité.',
    icon: Shield,
  },
  {
    title: 'Grande capacité',
    description:
      'Connectez tous vos appareils simultanément sans perte de performance.',
    icon: Upload,
  },
];

const services = [
  {
    title: 'Conseil technique',
    description:
      "Recommandations adaptées à votre projet pour optimiser l'infrastructure télécom",
    icon: Briefcase,
  },
  {
    title: 'Interlocuteur unique',
    description:
      "Un seul point de contact pour l'ensemble de votre projet de raccordement",
    icon: PhoneCall,
  },
  {
    title: 'Gestion administrative',
    description:
      'Nous prenons en charge toutes les démarches auprès des opérateurs',
    icon: FileCheck,
  },
  {
    title: 'Suivi de projet',
    description:
      'Supervision complète du cycle de vie de votre infrastructure',
    icon: Users,
  },
];

const packages = [
  {
    name: 'Sérénité Fibre',
    price: '340€',
    description: 'Solution complète pour maison individuelle',
    icon: Home,
    color: 'bg-amber-100 text-amber-700',
    features: [
      'Raccordement maison individuelle',
      'Interlocuteur dédié',
      'Gestion des démarches administratives',
      'Suivi personnalisé',
    ],
  },
  {
    name: 'Essentiel Fibre',
    price: '529€',
    description: 'Pour les constructions en collectif',
    icon: Building,
    color: 'bg-blue-100 text-blue-700',
    features: [
      'Raccordement collectif',
      'Conseil technique',
      'Coordination avec les opérateurs',
      'Optimisation des coûts',
    ],
  },
  {
    name: 'Confort Fibre',
    price: '799€',
    description: 'Service premium pour projets collectifs',
    icon: Building,
    color: 'bg-emerald-100 text-emerald-700',
    features: [
      'Tout inclus dans Essentiel Fibre',
      'Assistance technique renforcée',
      'Suivi prioritaire',
      'Garantie de délai optimisé',
    ],
  },
];

function targetForRole(role: string | undefined): string {
  if (role === 'admin' || role === 'super_admin') return '/admin';
  if (role === 'technicien') return '/technicien';
  return '/dashboard';
}

export function LandingPage() {
  const { user, isLoading } = useAuth();
  // Si l'utilisateur est deja connecte, la landing publique n'a pas de sens :
  // on le bascule directement sur son espace selon son role.
  if (!isLoading && user) {
    return <Navigate to={targetForRole(user.role)} replace />;
  }
  return (
    <MarketingLayout>
      <div className="flex flex-col min-h-screen">
        {/* Hero Section */}
        <section className="relative py-20 sm:py-32 overflow-hidden bg-gradient-to-b from-white to-bcl-grey">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiMzMjkyRkYiIGZpbGwtb3BhY2l0eT0iLjAyIj48cGF0aCBkPSJNMzYgMzRjMC0yLjIgMS44LTQgNC00czQgMS44IDQgNC0xLjggNC00IDQtNC0xLjgtNC00TTQgNGMyLjIgMCA0IDEuOCA0IDRzLTEuOCA0LTQgNC00LTEuOC00LTQgMS44LTQgNC00TTQgNDRjMi4yIDAgNCAxLjggNCA0cy0xLjggNC00IDQtNC0xLjgtNC00IDEuOC00IDQtNCIvPjwvZz48L2c+PC9zdmc+')] opacity-25"></div>
          <div className="container relative px-4 mx-auto">
            <div className="max-w-3xl mx-auto text-center">
              <div className="inline-block px-3 py-1 mb-6 text-xs font-medium text-bcl-blue bg-blue-50 rounded-full animate-fade-in">
                Bureau d&apos;études &amp; VRD en télécommunications
              </div>
              <h1
                className="text-balance text-4xl md:text-5xl lg:text-6xl font-bold mb-6 animate-fade-in"
                style={{ animationDelay: '100ms' }}
              >
                Préparez dès maintenant l&apos;adduction télécoms de votre
                projet immobilier
              </h1>
              <p
                className="text-lg md:text-xl text-gray-600 mb-8 max-w-2xl mx-auto animate-fade-in"
                style={{ animationDelay: '200ms' }}
              >
                Bénéficiez des services de téléphonie et d&apos;internet à Très
                Haut Débit via la fibre optique ! BCL TELECOM vous accompagne
                tout au long de votre projet d&apos;adduction et de
                raccordement.
              </p>
              <div
                className="flex flex-col sm:flex-row justify-center gap-4 animate-fade-in"
                style={{ animationDelay: '300ms' }}
              >
                <Link to="/signup">
                  <Button size="lg" className="w-full sm:w-auto">
                    Commencer maintenant{' '}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/offres">
                  <Button
                    variant="outline"
                    size="lg"
                    className="w-full sm:w-auto"
                  >
                    Découvrir nos services
                  </Button>
                </Link>
              </div>
            </div>

            <div
              className="mt-16 max-w-5xl mx-auto relative animate-fade-in"
              style={{ animationDelay: '400ms' }}
            >
              <div className="aspect-[16/9] overflow-hidden rounded-xl shadow-2xl">
                <div className="bg-gradient-to-r from-bcl-blue to-bcl-teal h-full w-full flex items-center justify-center">
                  <div className="glass-panel p-8 rounded-xl text-center">
                    <h3 className="text-2xl font-semibold text-bcl-blue mb-4">
                      Infrastructure réseau télécom
                    </h3>
                    <p className="text-gray-700">
                      Expertise en adduction et raccordement à la fibre optique
                      pour vos projets immobiliers
                    </p>
                    <div className="mt-6 flex justify-center">
                      <div className="text-center mr-8">
                        <div className="text-3xl font-bold text-bcl-blue">
                          100%
                        </div>
                        <div className="text-sm text-gray-600">
                          conformité réglementaire
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-3xl font-bold text-bcl-blue">
                          THD
                        </div>
                        <div className="text-sm text-gray-600">
                          Très Haut Débit
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="absolute -bottom-5 -left-5 h-24 w-24 bg-white rounded-full shadow-xl flex items-center justify-center animate-float">
                <div className="text-center">
                  <div className="text-bcl-blue font-bold text-xl">100%</div>
                  <div className="text-xs text-gray-500">satisfaction</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Référencement ARCEP & Partenaires Section */}
        <section className="py-12 bg-bcl-grey/30">
          <div className="container px-4 mx-auto">
            <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
              <div className="lg:w-1/2">
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="bg-bcl-blue/10 p-3 rounded-full">
                      <Shield className="h-6 w-6 text-bcl-blue" />
                    </div>
                    <h3 className="text-xl font-semibold">
                      Référencé par l&apos;ARCEP
                    </h3>
                  </div>
                  <p className="text-gray-600">
                    BCL TELECOM est officiellement référencé par
                    l&apos;Autorité de Régulation des Communications
                    Électroniques, des Postes et de la distribution de la
                    presse (ARCEP), garantissant notre conformité aux
                    exigences réglementaires et notre expertise technique dans
                    le domaine des télécommunications.
                  </p>
                </div>
              </div>

              <div className="lg:w-1/2">
                <h3 className="text-xl font-semibold mb-6 text-center lg:text-left">
                  Partenaire des principaux opérateurs
                </h3>
                <div className="grid grid-cols-3 gap-4 items-center justify-items-center">
                  <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 w-full h-24 flex items-center justify-center">
                    <div className="font-bold text-orange-500 text-xl">
                      Orange
                    </div>
                  </div>
                  <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 w-full h-24 flex items-center justify-center">
                    <div className="font-bold text-red-500 text-xl">SFR</div>
                  </div>
                  <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 w-full h-24 flex items-center justify-center">
                    <div className="font-bold text-blue-500 text-xl">Free</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Accompagnement Section */}
        <section className="py-16 bg-white">
          <div className="container px-4 mx-auto">
            <div className="max-w-3xl mx-auto text-center mb-16">
              <h2 className="text-balance text-3xl md:text-4xl font-bold mb-4">
                Simplifiez-vous l&apos;accès à la fibre optique
              </h2>
              <p className="text-lg text-gray-600">
                Nous prenons en charge toutes les démarches pour que vous
                n&apos;ayez à vous soucier de rien. Un seul interlocuteur dédié
                pour l&apos;ensemble de votre projet de raccordement.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
              {services.map((service, index) => (
                <div
                  key={service.title}
                  className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow duration-300 animate-fade-in"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className="w-12 h-12 rounded-full bg-bcl-blue/10 flex items-center justify-center mb-4">
                    <service.icon className="h-6 w-6 text-bcl-blue" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">{service.title}</h3>
                  <p className="text-gray-600">{service.description}</p>
                </div>
              ))}
            </div>

            <div className="max-w-3xl mx-auto text-center mb-10">
              <h2 className="text-balance text-2xl md:text-3xl font-bold mb-4">
                Des offres adaptées à chaque projet
              </h2>
              <p className="text-lg text-gray-600">
                Que vous construisiez une maison individuelle ou un immeuble
                collectif, nous avons la solution qu&apos;il vous faut.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {packages.map((pkg, index) => (
                <Card
                  key={pkg.name}
                  className="border-gray-200 overflow-hidden transition-all duration-300 hover:shadow-lg animate-fade-in"
                  style={{ animationDelay: `${index * 150}ms` }}
                >
                  <CardHeader className={`${pkg.color} p-6`}>
                    <div className="flex justify-between items-center">
                      <CardTitle className="text-xl font-bold">
                        {pkg.name}
                      </CardTitle>
                      <pkg.icon className="h-8 w-8" />
                    </div>
                    <CardDescription className="mt-2 text-base font-medium text-gray-700">
                      {pkg.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="p-6">
                    <div className="text-3xl font-bold text-bcl-blue mb-6">
                      {pkg.price}
                    </div>
                    <ul className="space-y-3">
                      {pkg.features.map((feature) => (
                        <li key={feature} className="flex items-start gap-2">
                          <CheckCircle2 className="h-5 w-5 text-bcl-teal shrink-0 mt-0.5" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                  <CardFooter className="bg-gray-50 p-6 border-t border-gray-100">
                    <Link to="/contact" className="w-full">
                      <Button className="w-full">En savoir plus</Button>
                    </Link>
                  </CardFooter>
                </Card>
              ))}
            </div>

            <div className="mt-12 text-center">
              <p className="text-gray-600 mb-4">
                Besoin d&apos;une solution personnalisée pour votre projet&nbsp;?
              </p>
              <Link to="/contact">
                <Button variant="outline" size="lg">
                  Contactez-nous pour un devis personnalisé
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-16 bg-white">
          <div className="container px-4 mx-auto">
            <div className="max-w-3xl mx-auto text-center mb-16">
              <h2 className="text-balance text-3xl md:text-4xl font-bold mb-4">
                Pourquoi choisir BCL Telecom pour votre projet&nbsp;?
              </h2>
              <p className="text-lg text-gray-600">
                Notre expertise en infrastructure réseau télécom garantit un
                raccordement fibre optique optimal pour votre projet
                immobilier.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {benefits.map((benefit, index) => (
                <div
                  key={benefit.title}
                  className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow duration-300 animate-fade-in"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center mb-4">
                    <benefit.icon className="h-6 w-6 text-bcl-blue" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-bcl-grey">
          <div className="container px-4 mx-auto max-w-6xl">
            <div className="rounded-2xl bg-bcl-blue p-8 md:p-12 shadow-xl overflow-hidden relative">
              <div className="absolute right-0 top-0 h-full w-1/3 bg-bcl-teal mix-blend-multiply opacity-70"></div>
              <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNGRkZGRkYiIGZpbGwtb3BhY2l0eT0iLjA1Ij48cGF0aCBkPSJNMzYgMzRjMC0yLjIgMS44LTQgNC00czQgMS44IDQgNC0xLjggNC00IDQtNC0xLjgtNC00TTQgNGMyLjIgMCA0IDEuOCA0IDRzLTEuOCA0LTQgNC00LTEuOC00LTQgMS44LTQgNC00TTQgNDRjMi4yIDAgNCAxLjggNCA0cy0xLjggNC00IDQtNC0xLjgtNC00IDEuOC00IDQtNCIvPjwvZz48L2c+PC9zdmc+')] opacity-30"></div>

              <div className="relative z-10 max-w-3xl">
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 text-balance">
                  Prêt à lancer votre projet d&apos;adduction
                  télécom&nbsp;?
                </h2>
                <p className="text-white/90 text-lg mb-8 max-w-2xl">
                  Créez votre compte en quelques minutes et commencez votre
                  projet avec BCL Telecom. Notre équipe d&apos;experts vous
                  accompagne à chaque étape du processus de raccordement.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link to="/signup">
                    <Button
                      size="lg"
                      variant="secondary"
                      className="w-full sm:w-auto"
                    >
                      Créer mon compte
                    </Button>
                  </Link>
                  <Link to="/contact">
                    <Button
                      size="lg"
                      variant="outline"
                      className="bg-transparent border-white text-white hover:bg-white/10 w-full sm:w-auto"
                    >
                      Nous contacter
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </MarketingLayout>
  );
}
